import { FREE_TIER_COUNTRIES, isUsOnlyPlan } from '~/api/user/services/subscription-service';

const TIER_FREE_TRANSLATION_KEY = 'webCommon.tier.free';
const TIER_PREMIUM_TRANSLATION_KEY = 'webCommon.tier.premium';
const TIER_HIFI_TRANSLATION_KEY = 'webCommon.tier.hifi';
const PLAN_STANDARD_TRANSLATION_KEY = 'webCommon.plan.standard';
const PLAN_STUDENT_TRANSLATION_KEY = 'webCommon.plan.student';
const PLAN_FAMILY_TRANSLATION_KEY = 'webCommon.plan.family';
const PLAN_MILITARY_TRANSLATION_KEY = 'webCommon.plan.military';
const PLAN_FIRST_RESPONDER_TRANSLATION_KEY = 'webCommon.plan.firstResponder';
const FAMILY_TYPE = 'FAMILY';
const FAMILY_GRADIENT = 'linear-gradient(96deg, #a197b5 6%, #daceea 84%)';
const FAMILY_IMAGE = '/images/banner/plan-family.png';
const FAMILY_DESCRIPTION = 'plans.familyDescription';
const FAMILY_COPY = 'plans.familyCopy';
const STANDARD_TYPE = 'STANDARD';
const STANDARD_GRADIENT = 'linear-gradient(102deg, #cacaca 6%, #7e7e7e 88%)';
const STANDARD_IMAGE = '/images/banner/plan-standard.png';
const STANDARD_DESCRIPTION = 'webCommon.generic.features.unlimitedSkipsAdFree';
const STANDARD_COPY = 'plans.standardCopy';
const STUDENT_TYPE = 'STUDENT';
const STUDENT_GRADIENT = 'linear-gradient(284deg, #e8b79c 15%, #f9ebe5)';
const STUDENT_IMAGE = '/images/banner/plan-student.png';
const STUDENT_DESCRIPTION = 'plans.studentDescription';
const STUDENT_COPY = 'plans.studentCopy';
const MILITARY_TYPE = 'MILITARY';
const MILITARY_GRADIENT = 'linear-gradient(103deg, #d8dfcd 6%, #a4a991 83%)';
const MILITARY_IMAGE = '/images/banner/plan-military.png';
const MILITARY_DESCRIPTION = 'plans.militaryDescription';
const MILITARY_COPY = 'plans.militaryCopy';
const FIRST_RESPONDER_TYPE = 'FIRST_RESPONDER';
const FIRST_RESPONDER_GRADIENT = 'linear-gradient(102deg, #efe7d6 3%, #d2bb8f 95%)';
const FIRST_RESPONDER_IMAGE = '/images/banner/plan-first-responder.png';
const FIRST_RESPONDER_DESCRIPTION = 'plans.firstResponderDescription';
const FIRST_RESPONDER_COPY = 'plans.firstResponderCopy';
const USPS_HIFI = [
    'webCommon.usp.hifiQuality.title',
    'webCommon.usp.songsAndVideos.title',
    'webCommon.usp.myActivity',

];
const USPS_HIFI_PLUS = [
    'webCommon.usp.immersiveInnovativeAudio.moreShort',
    'webCommon.usp.directArtistPayouts.title',
    'webCommon.usp.myActivity',
];

/**
 * Gets the subscription type string.
 *
 * @param {UserWebApi.SubscriptionType} type
 * @param {string} countryCode
 * @return {string} The subscription type string.
 */
export function getSubscriptionTypeString(type, countryCode) {
    switch (type) {
        case 'PREMIUM_PLUS':
            return 'webCommon.tier.hifi';
        case 'PREMIUM':
            return 'webCommon.tier.premium';
        case 'INTRO':
            return FREE_TIER_COUNTRIES.includes(countryCode) ? 'webCommon.tier.free' : 'webCommon.generic.noSubscription';
        default:
            return '';
    }
}

/**
 * Gets the subscription plan string.
 *
 * @param {UserWebApi.SubscriptionPlan} plan
 * @return {string}
 */
export function getSubscriptionPlanString(plan) {
    switch (plan) {
        case 'STANDARD':
            return 'webCommon.plan.standard';
        case 'STUDENT':
            return 'webCommon.plan.student';
        case 'FAMILY':
            return 'webCommon.plan.family';
        case 'MILITARY':
            return 'webCommon.plan.military';
        case 'FIRST_RESPONDER':
            return 'webCommon.plan.firstResponder';
        default:
            return plan;
    }
}

/**
 * @param {TIDAL.UserInfo} userInfo
 * @return {TIDAL.Subscription | TIDAL.FreeTier | null}
 */
export function getSubscriptionInfo(userInfo) {
    /** @type {TIDAL.Subscription | TIDAL.FreeTier | null} */
    let subscription = null;

    if (userInfo.subscription) {
        const { plan, type } = userInfo.subscription;
        subscription = SUBSCRIPTIONS.find(s => plan === s.plan && type === s.type) || null;
    }

    if (!subscription && FREE_TIER_COUNTRIES.includes(userInfo.countryCode)) {
        subscription = FREE_TIER;
    }

    return subscription;
}

/**
 * Gets the subscriptions extended with UI-specific properties.
 *
 * @param {import('@tidal/nuxtjs-axios').NuxtAxiosInstance} axios The axios instance
 * @param {UserWebApi.SubscriptionType | null} [typeFilter] The subscription type filer
 * @param {TIDAL.UserInfo['countryCode']} [userCountryCode] The user's country
 * @return {Promise<TIDAL.SubscriptionForUI[]>} The subscriptions
 */
export async function getSubscriptionsForUI(axios, typeFilter, userCountryCode) {
    /** @type {TIDAL.SubscriptionForUI[]} */
    const subscriptions = SUBSCRIPTIONS
        .filter(s => !typeFilter || s.type === typeFilter)
        // Filter out US-only plans if user country is not US.
        .filter(s => !userCountryCode || userCountryCode === 'US' || !isUsOnlyPlan(s.plan))
        .map(s => Object.assign({}, s));

    // Check availability of plans (student/military/firstresponder go through "offer").
    const plans = Array.from(new Set(subscriptions.map(plan => plan.plan)));
    /** @type {[TIDAL.ReasonForDenyMapping, TIDAL.SubscriptionPrices]} */
    const [planToReason, prices] = await Promise.all([
        axios.$get(`/user/subscription/reasonfordeny/${plans.join(',')}`),
        axios.$get('/user/subscription/prices'),
    ]);
    for (const subscription of subscriptions) {
        const planReason = planToReason[subscription.plan];
        if (planReason) {
            subscription.isSheerIdVerified = planReason.isSheerIdVerified;
            subscription.reasonForDeny = planReason.reason;
        }
        const subscriptionPrices = prices[subscription.type];
        if (subscriptionPrices) {
            const price = subscriptionPrices[subscription.plan];
            if (price) {
                subscription.price = price;
            }
        }
    }

    // Filter out plans:
    // - not available in the user's country
    // - that have no price
    return subscriptions.filter(s => s.reasonForDeny !== 'PLAN_NOT_AVAILABLE' && s.price !== undefined);
}

/**
 * Returns the translation key for the specific deny reason code.
 *
 * @param {UserWebApi.ReasonForDeny} [reason] The reason
 * @return {string} The translation key.
 */
export function denyReasonToTranslationKey(reason) {
    switch (reason) {
        case 'EXTERNAL_PAYMENT':
            return 'planDenyReason.EXTERNAL_PAYMENT';
        case 'GRACE_PERIOD_RESTRICTION':
            return 'error.subscription.USER_ACTION_RESTRICTED_IN_GRACE_PERIOD';
        case 'INVALID_PAYMENT_INFO':
            return 'planDenyReason.INVALID_PAYMENT_INFO';
        case 'NO_SUBSCRIPTION':
            return 'planDenyReason.NO_SUBSCRIPTION';
        case 'NOT_IN_STANDARD_PLAN':
            return 'planDenyReason.NOT_IN_STANDARD_PLAN';
        case 'PARTNER_USER':
            return 'planDenyReason.PARTNER_USER';
        case 'PREPAID_SUBSCRIPTION':
        case 'PLANS_NOT_SUPPORTED_BY_PAYMENT_METHOD':
            return 'planDenyReason.PREPAID_SUBSCRIPTION';
        case 'PLAN_NOT_AVAILABLE':
            return 'planDenyReason.PLAN_NOT_AVAILABLE';
        case 'USER_IS_CHILD':
            return 'planDenyReason.USER_IS_CHILD';
        case 'USER_IS_PARENT':
            return 'planDenyReason.USER_IS_PARENT';
        case 'UNKNOWN':
            return 'planDenyReason.UNKNOWN';
        case 'NONE':
        default:
            return '';
    }
}

/** @type {TIDAL.FreeTier} */
const FREE_TIER = {
    isFreeTier: true,
    type: 'INTRO',
    typeTranslationKey: TIER_FREE_TRANSLATION_KEY,
    benefitStrings: [
        'webCommon.usp.normalQuality.title',
        'webCommon.usp.songs.title',
        'webCommon.usp.limitedAds.title',
    ],
};

/** @type {TIDAL.PaidTier[]} */
const SUBSCRIPTIONS = [
    {
        isFreeTier: false,
        plan: STANDARD_TYPE,
        planTranslationKey: PLAN_STANDARD_TRANSLATION_KEY,
        planDescription: STANDARD_DESCRIPTION,
        planCopy: STANDARD_COPY,
        type: 'PREMIUM',
        typeTranslationKey: TIER_PREMIUM_TRANSLATION_KEY,
        benefitStrings: [
            ...USPS_HIFI,
        ],
        style: {
            gradient: STANDARD_GRADIENT,
            banner: STANDARD_IMAGE,
        },
    },
    {
        isFreeTier: false,
        plan: STANDARD_TYPE,
        planTranslationKey: PLAN_STANDARD_TRANSLATION_KEY,
        planDescription: STANDARD_DESCRIPTION,
        planCopy: STANDARD_COPY,
        type: 'PREMIUM_PLUS',
        typeTranslationKey: TIER_HIFI_TRANSLATION_KEY,
        benefitStrings: [
            ...USPS_HIFI_PLUS,
        ],
        style: {
            gradient: STANDARD_GRADIENT,
            banner: STANDARD_IMAGE,
        },
    },
    {
        isFreeTier: false,
        plan: FAMILY_TYPE,
        planTranslationKey: PLAN_FAMILY_TRANSLATION_KEY,
        planDescription: FAMILY_DESCRIPTION,
        planCopy: FAMILY_COPY,
        planChangeNote: 'plans.familyChangeNote',
        type: 'PREMIUM',
        typeTranslationKey: TIER_PREMIUM_TRANSLATION_KEY,
        benefitStrings: [
            'offers.plans.family.features[0].summary',
            ...USPS_HIFI,
        ],
        style: {
            gradient: FAMILY_GRADIENT,
            banner: FAMILY_IMAGE,
        },
    },
    {
        isFreeTier: false,
        plan: FAMILY_TYPE,
        planTranslationKey: PLAN_FAMILY_TRANSLATION_KEY,
        planDescription: FAMILY_DESCRIPTION,
        planCopy: FAMILY_COPY,
        planChangeNote: 'plans.familyChangeNote',
        type: 'PREMIUM_PLUS',
        typeTranslationKey: TIER_HIFI_TRANSLATION_KEY,
        benefitStrings: [
            'offers.plans.family.features[0].summary',
            ...USPS_HIFI_PLUS,
        ],
        style: {
            gradient: FAMILY_GRADIENT,
            banner: FAMILY_IMAGE,
        },
    },
    {
        isFreeTier: false,
        offerId: 'student',
        plan: STUDENT_TYPE,
        planTranslationKey: PLAN_STUDENT_TRANSLATION_KEY,
        planDescription: STUDENT_DESCRIPTION,
        planCopy: STUDENT_COPY,
        type: 'PREMIUM',
        typeTranslationKey: TIER_PREMIUM_TRANSLATION_KEY,
        benefitStrings: [
            'offers.plans.student.features[0].summary',
            ...USPS_HIFI,
        ],
        style: {
            gradient: STUDENT_GRADIENT,
            banner: STUDENT_IMAGE,
        },
    },
    {
        isFreeTier: false,
        offerId: 'student',
        plan: STUDENT_TYPE,
        planTranslationKey: PLAN_STUDENT_TRANSLATION_KEY,
        planDescription: STUDENT_DESCRIPTION,
        planCopy: STUDENT_COPY,
        type: 'PREMIUM_PLUS',
        typeTranslationKey: TIER_HIFI_TRANSLATION_KEY,
        benefitStrings: [
            'offers.plans.student.features[0].summary',
            ...USPS_HIFI_PLUS,
        ],
        style: {
            gradient: STUDENT_GRADIENT,
            banner: STUDENT_IMAGE,
        },
    },
    {
        isFreeTier: false,
        offerId: 'military',
        plan: MILITARY_TYPE,
        planTranslationKey: PLAN_MILITARY_TRANSLATION_KEY,
        planDescription: MILITARY_DESCRIPTION,
        planCopy: MILITARY_COPY,
        type: 'PREMIUM',
        typeTranslationKey: TIER_PREMIUM_TRANSLATION_KEY,
        benefitStrings: [
            'offers.plans.military.features[0].summary',
            ...USPS_HIFI,
        ],
        style: {
            gradient: MILITARY_GRADIENT,
            banner: MILITARY_IMAGE,
        },
    },
    {
        isFreeTier: false,
        offerId: 'military',
        plan: MILITARY_TYPE,
        planTranslationKey: PLAN_MILITARY_TRANSLATION_KEY,
        planDescription: MILITARY_DESCRIPTION,
        planCopy: MILITARY_COPY,
        type: 'PREMIUM_PLUS',
        typeTranslationKey: TIER_HIFI_TRANSLATION_KEY,
        benefitStrings: [
            'offers.plans.military.features[0].summary',
            ...USPS_HIFI_PLUS,
        ],
        style: {
            gradient: MILITARY_GRADIENT,
            banner: MILITARY_IMAGE,
        },
    },
    {
        isFreeTier: false,
        offerId: 'firstResponder',
        plan: FIRST_RESPONDER_TYPE,
        planTranslationKey: PLAN_FIRST_RESPONDER_TRANSLATION_KEY,
        planDescription: FIRST_RESPONDER_DESCRIPTION,
        planCopy: FIRST_RESPONDER_COPY,
        type: 'PREMIUM',
        typeTranslationKey: TIER_PREMIUM_TRANSLATION_KEY,
        benefitStrings: [
            'offers.plans.first_responder.features[0].summary',
            ...USPS_HIFI,
        ],
        style: {
            gradient: FIRST_RESPONDER_GRADIENT,
            banner: FIRST_RESPONDER_IMAGE,
        },
    },
    {
        isFreeTier: false,
        offerId: 'firstResponder',
        plan: FIRST_RESPONDER_TYPE,
        planTranslationKey: PLAN_FIRST_RESPONDER_TRANSLATION_KEY,
        planDescription: FIRST_RESPONDER_DESCRIPTION,
        planCopy: FIRST_RESPONDER_COPY,
        type: 'PREMIUM_PLUS',
        typeTranslationKey: TIER_HIFI_TRANSLATION_KEY,
        benefitStrings: [
            'offers.plans.first_responder.features[0].summary',
            ...USPS_HIFI_PLUS,
        ],
        style: {
            gradient: FIRST_RESPONDER_GRADIENT,
            banner: FIRST_RESPONDER_IMAGE,
        },
    },
];
